import {
    DeleteObject,
    S3ContentSource,
    S3Content,
    ContentSourceType,
    DeleteRequestAws,
    MimeType,
    S3Object
} from "js/models";
import { defaultTo } from "lodash-es";
import { S3Helper } from "js/mediahandler/aws/S3Helper";
import S3 from "@aws-sdk/client-s3";
import moment from "moment";
import { getAssetType, getMediaContentRelPath } from "js/types/general";
import { CognitoIdentityClient } from "@aws-sdk/client-cognito-identity";
import { fromCognitoIdentityPool } from "@aws-sdk/credential-provider-cognito-identity";
import nodePath from "path";
import sanitize from "sanitize-s3-objectkey";

import { Credentials, Provider } from "@aws-sdk/types";
//Folder keys end at /. Bucket root parent is "".
export class S3Service {
    protected bucket: S3ContentSource;
    protected sdk: S3Helper;
    protected params: {
        root: string;
        generatedFilesRoot: string;
    };
    constructor(
        b: S3ContentSource,
        params: {
            root: string;
            generatedFilesRoot: string;
        }
    ) {
        this.params = params;
        this.bucket = b;

        const creds: Credentials | Provider<Credentials> =
            b.data.AccessKeyId && b.data.SecretAccessKey
                ? {
                      accessKeyId: b.data.AccessKeyId,
                      secretAccessKey: b.data.SecretAccessKey,
                      sessionToken: b.data.SessionToken
                  }
                : fromCognitoIdentityPool({
                      identityPoolId: b.data.identityPoolId,
                      client: new CognitoIdentityClient({
                          region: b.data.bucketRegion
                      })
                  });
        this.sdk = new S3Helper(b.data.bucketName, b.data.bucketRegion, creds);
    }

    getFullUrl(rel: string) {
        return this.sdk.getPublicUrl(rel);
    }

    protected normalizeKey(key: string) {
        return decodeURIComponent(key.replace(/\+/g, " "));
    }

    getThumbnailUrl(key: string) {
        const name = S3Service.getName(key);
        const relPath = `${key}`.replace(this.params.root, "");
        const thumbKey = `${this.params.generatedFilesRoot}${relPath}/thumb.png`;
        const mimeType = getAssetType(name);
        const isVideo = [MimeType.MP4, MimeType.Webm].includes(mimeType);
        return isVideo ? this.sdk.getPublicUrl(thumbKey) : null;
    }

    toS3Content(a: S3Object): S3Content {
        const name = S3Service.getName(a.Key);
        const mime = getAssetType(name);
        // a.Key = this.normalizeKey(a.Key);

        return {
            ContentSourceType: ContentSourceType.S3Bucket,
            createdAt: null,
            modifiedAt: moment(a.LastModified).unix(),
            identifier: a.Key,
            size: a.Size,
            mimeType: getAssetType(name),
            name: name,
            relPath: a.Key,
            url: this.sdk.getPublicUrl(encodeURIComponent(a.Key)),
            metadata: {
                thumbnailUrl: [MimeType.MP4, MimeType.Webm].includes(mime)
                    ? this.getThumbnailUrl(encodeURIComponent(a.Key))
                    : this.sdk.getPublicUrl(encodeURIComponent(a.Key))
            },
            fullData: a
        };
    }

    static getName(path: string) {
        const splitted = path.split("/");

        if (splitted[splitted.length - 1].length == 0 && splitted.length >= 2) {
            //directory
            return splitted[splitted.length - 2];
        } else {
            return splitted[splitted.length - 1];
        }
    }

    static getParent(path: string) {
        const parent = nodePath.dirname(path);
        return parent === "." ? "" : parent + "/";
    }

    async getListings(path: string) {
        const data: S3.ListObjectsV2CommandOutput["CommonPrefixes"] = [];
        const filesData: S3Object[] = [];
        let response: S3.ListObjectsV2Output;
        let continuationToken = null;
        do {
            response = await this.sdk.getDirectBucketListing(
                path,
                continuationToken
            );
            continuationToken = response.NextContinuationToken
                ? response.NextContinuationToken
                : null;
            if (response.CommonPrefixes) {
                data.push(...response.CommonPrefixes);
            }
            if (response.Contents) {
                filesData.push(...response.Contents);
            }
        } while (response.IsTruncated);

        const directories: S3Content[] = data.map(
            (a): S3Content => {
                return {
                    ContentSourceType: ContentSourceType.S3Bucket,
                    createdAt: null,
                    fullData: null,
                    identifier: a.Prefix,
                    metadata: null,
                    mimeType: null,
                    modifiedAt: null,
                    name: S3Service.getName(a.Prefix),
                    relPath: a.Prefix,
                    size: null,
                    url: this.sdk.getPublicUrl(a.Prefix)
                };
            }
        );

        const files: S3Content[] = filesData.map(a => this.toS3Content(a));

        return [...directories, ...files].filter(x => x.relPath !== path);
    }

    // getBuckets() {
    // return this.doServerXHR<ContentSource[]>({
    //   url: this.routes.server.api.mediaManagement.listBuckets(),
    //   method: "get",
    // });
    // }

    // createBucket(name: string) {
    // return this.doServerXHR<ContentSource>({
    //   url: this.routes.server.api.mediaManagement.createBucket(),
    //   data: {
    //     name: name,
    //   },
    //   method: "post",
    // });
    // }

    // sadsads
    // sadsads/
    // sadsads/sadsa.mp4

    async createDirectory(folderName: string, prefix?: string) {
        prefix = defaultTo(prefix, "");
        const path = getMediaContentRelPath(prefix, sanitize(folderName));
        const resp = await this.sdk.createFolderInBucket(path);
        let content: S3Content = null;
        if (resp) {
            content = {
                modifiedAt: moment().unix(),
                mimeType: null,
                relPath: path + "/",
                size: 0,
                metadata: null,
                ContentSourceType: ContentSourceType.S3Bucket,
                createdAt: null,
                identifier: path + "/",
                name: folderName,
                url: this.sdk.getPublicUrl(path + "/"),
                fullData: {
                    Size: 0,
                    Key: path + "/",
                    LastModified: moment().toDate()
                }
            };
        }
        return content;
    }

    async rename(srcKey: string, destKey: string) {
        const resp = await this.sdk.createFolderInBucket(destKey);
        const content: S3Content = null;
        // if (resp) {
        //     content = {
        //         modifiedAt: moment().unix(),
        //         mimeType: null,
        //         relPath: path + "/",
        //         size: 0,
        //         metadata: null,
        //         ContentSourceType: ContentSourceType.S3Bucket,
        //         createdAt: null,
        //         identifier: path + "/",
        //         name: folderName,
        //         url: this.sdk.getPublicUrl(path + "/"),
        //         fullData: resp
        //     };
        //}
        return content;
    }

    async bulkDelete(objKey: DeleteObject[]) {
        const internalKeys: string[] = [];
        for (const obj of objKey) {
            if (obj.type === "folder") {
                internalKeys.push(...(await this.getAllObjectsKeys(obj.key)));
            }
        }
        const deleted = await this.sdk.deleteMultipleObjectsFromS3([
            ...internalKeys.map(a => {
                return { Key: a } as DeleteRequestAws;
            }),
            ...objKey.map(i => {
                return {
                    Key: i.key
                } as DeleteRequestAws;
            })
        ]);
        return deleted.map(a => a.Key);
    }

    // deleteBucket(contentSourceId: number) {
    // return this.doServerXHR<boolean>({
    //   url: this.routes.server.api.mediaManagement.deleteBucket(),
    //   params: {
    //     contentSourceId: contentSourceId,
    //   },
    //   method: "delete",
    // });
    // }

    async uploadFile(
        file: File,
        path: string,
        onProgressUpdate: (progress: number) => void
    ) {
        path = defaultTo(path, "");
        const key = sanitize(getMediaContentRelPath(path, file.name));
        // const key = getMediaContentRelPath(path, file.name);

        const resp = await this.sdk.UploadObject(file, key, onProgressUpdate);
        let content: S3Content = null;

        if (resp) {
            const mime = getAssetType(file.name);
            // resp.Key = this.normalizeKey(resp.Key);
            content = {
                modifiedAt: moment().unix(),
                size: file.size,
                ContentSourceType: ContentSourceType.S3Bucket,
                createdAt: null,
                identifier: resp.Key,
                name: S3Service.getName(resp.Key),
                mimeType: getAssetType(file.name),
                relPath: resp.Key,
                url: this.sdk.getPublicUrl(encodeURIComponent(resp.Key)),
                metadata: {
                    thumbnailUrl: [MimeType.MP4, MimeType.Webm].includes(mime)
                        ? null
                        : this.sdk.getPublicUrl(encodeURIComponent(resp.Key))
                },
                fullData: resp
            };
        }
        return content;
    }
    async getAllObjectsKeys(prefix: string): Promise<string[]> {
        const data: string[] = [];
        let response: S3.ListObjectsV2Output;
        let continuationToken = null;
        do {
            response = await this.sdk.getBucketListing(
                prefix,
                continuationToken
            );
            continuationToken = response.NextContinuationToken
                ? response.NextContinuationToken
                : null;
            data.push(...response.Contents.map(a => a.Key));
        } while (response.IsTruncated);
        return data.reverse();
    }
}
